/**
 * useItemRelationshipGates
 *
 * Evaluates the compound relationship gate state for a single item and returns
 * the rule engine result. Components use this to:
 *   - block or warn before Bloc commitment
 *   - block or warn before Track entry
 *   - prompt elaboration selection at commitment time
 *   - surface scope completeness issues when a Collection is active
 *
 * The hook reads from the live stores and re-evaluates whenever relevant
 * relationship state changes.
 *
 * Per canon 07A §3, gates fire in priority order. All gate results are
 * returned so the UI can show a complete diagnostic, but `blockedAtGate`
 * identifies the highest-priority (lowest number) blocking gate.
 */

import { useMemo } from 'react';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useCollectionsStore } from '../../stores/prioritization/collectionsStore';
import {
  evaluateItemRelationships,
  type RelationshipEvalContext,
  type RelationshipEvalResult,
} from '../../lib/itemRelationshipRuleEngine';

export interface UseItemRelationshipGatesOptions {
  /** The item being evaluated. */
  itemId: string;
  /**
   * Active Collection ID used as a type-B prioritization scope.
   * Pass null when no bounded scope is active.
   */
  activeScopeCollectionId?: string | null;
  /**
   * The frame ID declared for this item in the current track context.
   * Pass null when no frame has been declared.
   */
  activeFrameId?: string | null;
  /**
   * Declared track role for dual aggregation+elaboration parent items.
   */
  declaredTrackRole?: 'aggregation' | 'elaboration' | 'both' | null;
  /**
   * Selected elaboration child ID at commitment time.
   * Pass null if no elaboration selection has been made.
   */
  selectedElaborationChildId?: string | null;
  /**
   * The most restrictive protection ring the item is currently in.
   * Defaults to 3 (open / continuous shaping).
   */
  ringAtTime?: 1 | 2 | 3;
  /**
   * Whether this evaluation is happening at commitment time (vs. inclusion
   * or track-entry time). Commitment-time evaluations are more strict:
   *   - external hard deps become blocks (not warns)
   *   - elaboration selection is required
   *   - Bloc atomicity is fully enforced
   */
  isCommitmentTime?: boolean;
}

export function useItemRelationshipGates(
  options: UseItemRelationshipGatesOptions,
): RelationshipEvalResult {
  const {
    itemId,
    activeScopeCollectionId = null,
    activeFrameId = null,
    declaredTrackRole = null,
    selectedElaborationChildId = null,
    ringAtTime = 3,
    isCommitmentTime = false,
  } = options;

  const allGroups = useItemRelationshipsStore((s) => s.groups);
  const allMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const allEdges = useItemRelationshipsStore((s) => s.edges);
  // Select the item directly so the memo re-evaluates when frames or
  // canonicalFrameId change — getItemById is a stable function reference and
  // would NOT trigger a re-render on item data changes.
  const item = useItemsStore((s) => s.items.find((i) => i.id === itemId) ?? null);
  const getItemIdsInCollection = useCollectionsStore((s) => s.getItemIdsInCollection);

  return useMemo(() => {
    const itemFrames = item?.frames ?? [];

    // Build the scope set from the active collection if provided
    let scopeItemIds: Set<string> | null = null;
    if (activeScopeCollectionId) {
      const ids = getItemIdsInCollection(activeScopeCollectionId);
      scopeItemIds = new Set(ids);
      // Include the current item itself in scope (it's a member by definition)
      scopeItemIds.add(itemId);
    }

    // Item-own framing is canonical when the item has a category set but no
    // alternate frame has been promoted to canonical (canonicalFrameId is absent).
    const itemOwnFrameIsCanonical =
      !!item?.itemCategory && !item?.canonicalFrameId;

    const ctx: RelationshipEvalContext = {
      itemId,
      allGroups,
      allMemberships,
      allEdges,
      itemFrames,
      scopeItemIds,
      activeFrameId,
      itemOwnFrameIsCanonical,
      declaredTrackRole,
      selectedElaborationChildId,
      ringAtTime,
      isCommitmentTime,
    };

    return evaluateItemRelationships(ctx);
  }, [
    itemId,
    item,
    allGroups,
    allMemberships,
    allEdges,
    getItemIdsInCollection,
    activeScopeCollectionId,
    activeFrameId,
    declaredTrackRole,
    selectedElaborationChildId,
    ringAtTime,
    isCommitmentTime,
  ]);
}
